// Turns an audit row into the sentence an activity feed entry must be (04-data §6.1): who acted,
// what they did, to what, and when — never the event key. Automated work is attributed to Calis
// with its originating surface (§6.3), not to the person whose account the worker ran as.
// Pure; unit-tested under node.

import type { AuditEventDto, ProjectActivityItemDto } from '../../../shared/api-types.js';
import { AUDIT_SURFACE_LABELS, SLACK_FINDING_KIND_LABELS, SLACK_FINDING_KINDS, type AuditOutcome, type AuditSurface } from '../../../shared/enums.js';
import { autoProcessWindowLabel } from '../../../shared/fellow.js';
import { AN_ADMINISTRATOR, UNNAMED_COLLEAGUE, WITHHELD_COLLEAGUE, WITHHELD_COLLEAGUE_OBJECT } from '../../../shared/text.js';
import { formatDate, pluralise } from '../../ui/format.js';
import type { Tone } from '../../ui/StatusPill.js';
import { slackStepsText } from './slack-context.js';

export interface ActivityDetail {
  label: string;
  value: string;
}

export interface ActivityLine {
  /** "Nino Beridze" for a person; "Calis" for automated work. */
  actorName: string;
  /** True when the entry was produced by a background surface rather than a person at the keyboard. */
  system: boolean;
  /** Which surface the change came from — stated whenever it was not the web app (§6.3). */
  surfaceLabel: string | null;
  /** The same, as the phrase the entry says it in — "via the scheduler" — whole, never the label lowercased (`AUDIT_SURFACE_PHRASES`). */
  surfacePhrase: string | null;
  /** A complete past-tense phrase: "requested a summary". */
  action: string;
  /** A state word for a refused or failed outcome; null for success (the phrase already says so). */
  outcome: { label: string; tone: Tone } | null;
  /** Label/value pairs worth showing behind a disclosure; empty when there is nothing beyond the sentence. */
  details: ActivityDetail[];
}

/**
 * The surface an automated change came from, as the phrase an entry says it in. Why whole phrases (05-content §8; the
 * Phase 1c audit's D30): the entry said "via the {label lowercased}", a label folded into a sentence.
 */
export const AUDIT_SURFACE_PHRASES: Readonly<Record<AuditSurface, string>> = {
  web: 'via the web app',
  worker: 'via the background worker',
  scheduler: 'via the scheduler',
  cli: 'via the command line',
};

/**
 * An activity entry's accessible name: its place in the list, its sentence, when, the surface where it was not the web
 * app, and the outcome where it was not a success — "3 of 12: Calis summarised the meeting, 4 Oct 2026, 09:00, via the
 * background worker. Outcome: Failed." Why whole templates (05-content §8; Phase 1c D30): the name was the sentence with
 * ", via the …" and ", failed" spliced on by condition, the §6 word lowercased; the outcome now keeps its word as written.
 */
export function activityEntryLabel(entry: { position: number; total: number; line: Pick<ActivityLine, 'actorName' | 'action' | 'surfacePhrase' | 'outcome'>; when: string }): string {
  const { position, total, line, when } = entry;
  const statement = `${line.actorName} ${line.action}`;
  const surface = line.surfacePhrase;
  const outcome = line.outcome?.label ?? null;
  if (surface !== null && outcome !== null) return `${position} of ${total}: ${statement}, ${when}, ${surface}. Outcome: ${outcome}.`;
  if (surface !== null) return `${position} of ${total}: ${statement}, ${when}, ${surface}.`;
  if (outcome !== null) return `${position} of ${total}: ${statement}, ${when}. Outcome: ${outcome}.`;
  return `${position} of ${total}: ${statement}, ${when}.`;
}

/**
 * The line above a meeting's history: how far back it reaches and in which zone — the capped case its own whole sentence
 * (05-content §8; Phase 1c D30: ", up to the 200 most recent entries" was spliced in by condition).
 */
export function activityWindowLine(retentionDays: number, capped: boolean, zoneLabel: string): string {
  return capped
    ? pluralise(retentionDays, {
      one: `Last # day of activity, newest first, up to the 200 most recent entries. Times are shown in ${zoneLabel}.`,
      other: `Last # days of activity, newest first, up to the 200 most recent entries. Times are shown in ${zoneLabel}.`,
    })
    : pluralise(retentionDays, { one: `Last # day of activity, newest first. Times are shown in ${zoneLabel}.`, other: `Last # days of activity, newest first. Times are shown in ${zoneLabel}.` });
}

const OUTCOME_LABELS: Readonly<Record<AuditOutcome, { label: string; tone: Tone } | null>> = {
  success: null,
  refused: { label: 'Refused', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
  // A run that looked and did nothing (05-content §6; migration 0050): its own word, never Completed.
  skipped: { label: 'Skipped', tone: 'neutral' },
};

/**
 * Job, provider and connection codes as a person would say them, as the tail of "… — because …".
 * Every code a Slack audit row can carry is here — the Slack client's, the connect flow's, the Slack
 * executors' and admission's — and a test holds the list to the server's own (owner ruling
 * 20260926-002133).
 */
const REASONS: Readonly<Record<string, string>> = {
  ai_not_configured: 'AI summaries are not configured on this installation',
  ai_summaries_not_granted: 'the account does not include AI summaries',
  ai_unavailable: 'the AI service did not respond',
  // Shared by summaries and Slack checks, so it names neither.
  ai_invalid_output: 'the AI service returned an answer Calis could not use',
  transcript_empty: 'the recording has no transcript',
  meeting_not_found: 'the meeting could not be found',
  invalid_payload: 'the job request was malformed',
  internal_error: 'something failed inside Calis',
  not_approved: 'the account was not approved at the time',
  no_scope_access: 'the account had no access at the time',
  no_subject: 'the account no longer existed',
  session_revoked: 'the account’s access had been revoked',
  fellow_auth_failed: 'Fellow did not accept the API key',
  fellow_forbidden: 'Fellow refused access for the API key',
  fellow_unavailable: 'Fellow was not responding',
  fellow_workspace_not_found: 'Fellow could not find the workspace',
  fellow_invalid_response: 'Fellow sent a response Calis could not read',
  fellow_key_unreadable: 'the stored API key could not be read',
  fellow_connect_floor_unmet: 'the Fellow integration is not configured on this installation',
  fellow_connect_not_granted: 'the account does not include Connect Fellow',
  connection_not_found: 'the connection had been removed',
  connection_changed: 'the connection changed during the run',
  sync_page_limit: 'the sync reached its page limit for one run',
  // Ticking off in Fellow (owner, 2026-09-30): the `fellow.items` run's own refusals (server/fellow/items-job.ts).
  fellow_write_off: 'ticking off in Fellow is off on this installation',
  // Import or tie (ADR 0015, Stage 2): the look at a person's Fellow items (server/fellow/match-job.ts) refuses here too.
  fellow_match_off: 'matching Fellow’s items is off on this installation',
  // Fellow → Calis (ADR 0015, Stage 3): the read's own gate (todos/fellow-outbox.ts fellowStateGate).
  fellow_state_off: 'bringing back changes made in Fellow is off on this installation',
  connection_not_active: 'the Fellow connection was not active',
  fellow_item_not_found: 'Fellow no longer had the action item',
  fellow_item_forbidden: 'Fellow did not let the account change the action item',
  fellow_invalid_subdomain: 'the Fellow workspace address was not one Calis may call',
  fellow_not_configured: 'the Fellow integration is not configured on this installation',
  // Slack: the client's codes (server/slack/types.ts SLACK_ERROR_CODES).
  slack_token_revoked: 'Slack no longer accepted the access Calis held',
  slack_token_rejected: 'Slack did not accept the access Calis held',
  slack_workspace_not_allowed: 'the Slack workspace is no longer one Calis may read',
  slack_token_expired: 'Calis’s Slack access had expired',
  slack_token_unreadable: 'the stored Slack access could not be read',
  slack_auth_failed: 'Slack did not accept the connection request',
  slack_missing_scope: 'Slack no longer granted all the access Calis needs',
  slack_forbidden: 'the Slack workspace does not allow Calis to do this',
  slack_conversation_unavailable: 'a Slack conversation could not be read',
  slack_rate_limited: 'Slack asked Calis to slow down',
  slack_unavailable: 'Slack was not responding',
  slack_invalid_response: 'Slack sent a response Calis could not read',
  slack_invalid_response_at_connect: 'Slack sent a response Calis could not read while connecting',
  slack_unexpected_grant: 'Slack granted a kind of access Calis does not use',
  slack_not_configured: 'the Slack integration is not configured on this installation',
  // Slack: the connect flow's own refusals (server/slack/oauth.ts SLACK_CONNECT_FAILURE_CODES), and one
  // it no longer makes.
  slack_state_invalid: 'the answer from Slack did not match the request Calis sent',
  slack_session_mismatch: 'connecting was started by another account or in another session',
  slack_authorize_error: 'Slack returned an error instead of a connection',
  slack_connect_floor_unmet: 'the Slack integration is not configured on this installation',
  slack_connect_not_granted: 'the account does not include Slack connect',
  slack_enterprise_install: 'Slack offered a whole Enterprise organisation rather than one workspace',
  slack_wrong_workspace: 'the Slack workspace is not one Calis may connect to',
  // Why kept although no connect is refused for it any more (owner ruling of 2026-09-27): audit rows
  // written before the ruling still carry it, and the record should read as it did.
  slack_excess_scope: 'Slack granted more access than Calis asked for',
  slack_wrong_account: 'the Slack account does not use the account’s Calis email address',
  slack_email_unverified: 'the Calis email address was not verified',
  slack_guest_account: 'guest Slack accounts cannot be connected',
  slack_account_in_use: 'the Slack account is already connected to another Calis account',
  // Slack checks (ADR 0009): the executor’s gates and preconditions (server/slack/context-job.ts).
  slack_analysis_floor_unmet: 'Slack checks are not configured on this installation',
  slack_analysis_not_granted: 'the account does not include Slack analysis',
  needs_summary: 'the meeting had no summary to compare with',
  slack_not_connected: 'Slack was not connected',
  slack_connection_needs_attention: 'the Slack connection needed attention',
  slack_no_sources: 'there was nothing Calis could read in Slack — no channel in the list it could read, no Slack search, and no direct messages or group chats',
  slack_meeting_time_unknown: 'the meeting’s time was not known',
  analysis_not_found: 'the Slack check could no longer be found',
  slack_daily_limit: 'the day’s limit of Slack checks had been reached',
  // Why a run was Skipped (05-content §6; migration 0050): the conditions a job looked at and found no longer held. A
  // check the person's own switch stopped (`auto_check_off`) is never on an audit row (the owner's ruling O2); the words
  // are here for Admin → Queue's count of skips by reason, which names no one.
  auto_check_off: 'automatic Slack checks were switched off',
  follow_up_off: 'later re-checks are switched off on this installation',
  slack_sweep_off: 'the daily Slack sweep is switched off on this installation',
  not_connected: 'Slack was not connected',
  connection_needs_attention: 'the Slack connection needed attention',
  workspace_not_allowed: 'the Slack workspace is no longer one Calis may read',
  not_synced: 'the Slack channel list had not been read yet',
  paused_no_visit: 'the sweep was paused until the person visits Calis again',
  connection_replaced: 'another Slack connection had replaced the one it was queued for',
  mode_changed: 'the installation changed how the sweep runs between two attempts',
  no_meeting: 'the meeting could not be found',
  nothing_waiting: 'no Fellow item was waiting to be decided',
  unchanged: 'nothing had changed since the last look',
  daily_cap: 'the day’s limit for the meeting had been reached',
  already_running: 'a run was already queued or under way',
  // The sorting pass over a person's to-dos in no project (owner ruling 11 of 2026-10-04; ADR 0018): its gate's refusals
  // (todos/project-sort-chain.ts) and why a run was Skipped (todos/project-sort-job.ts).
  projects_off: 'projects are switched off on this installation',
  sorting_floors_unmet: 'sorting to-dos is not configured on this installation',
  sorting_not_granted: 'the account includes neither AI summaries nor both Slack connect and Slack analysis',
  sorting_paused: 'sorting to-dos is paused on this installation',
  project_sort_capped: 'the day’s limit of sorting calls had been reached',
  nothing_to_sort: 'no open to-do was left to sort',
};

/** Whether a code has its own words (the rest are spelt out from the code). */
export function hasReasonFor(code: string): boolean {
  return Object.hasOwn(REASONS, code);
}

/** A code in words; unknown codes are spelt out rather than printed raw. */
export function reasonInWords(code: unknown): string {
  if (typeof code !== 'string' || code.trim() === '') return 'no reason was recorded';
  return REASONS[code] ?? code.replace(/[._-]+/gu, ' ').trim();
}

/** "Nino Beridze <nino@centro.team>" → "Nino Beridze". */
export function actorDisplayName(label: string): string {
  const stripped = label.replace(/\s*<[^>]*>\s*$/u, '').trim();
  return stripped === '' ? label.trim() : stripped;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function numberOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** "1 finding", "3 findings": the count and its noun as one complete form, chosen by the plural rule (05-content §8). */
function plural(count: number, one: string, other: string): string {
  return pluralise(count, { one: `# ${one}`, other: `# ${other}` });
}

/** A Slack check's recorded steps, when the row carries all of them as counts and flags. */
function slackStepsOf(after: Record<string, unknown>): Parameters<typeof slackStepsText>[0] | null {
  const rounds = numberOf(after.searchRounds);
  const read = numberOf(after.conversationsReadInFallback);
  const verdicts = after.judgeVerdicts;
  if (rounds === null || read === null || typeof after.searchUnavailable !== 'boolean' || !Array.isArray(verdicts) || !verdicts.every((verdict) => typeof verdict === 'boolean')) return null;
  return { searchRounds: rounds, searchUnavailable: after.searchUnavailable, judgeVerdicts: verdicts as boolean[], conversationsReadInFallback: read };
}

/** The sentence for one row of a meeting's history. */
export function describeMeetingActivity(event: AuditEventDto): ActivityLine {
  const after = asRecord(event.after);
  const system = event.surface !== 'web';
  const actorName = system ? 'Calis' : actorDisplayName(event.actor.label);
  const surfaceLabel = system ? AUDIT_SURFACE_LABELS[event.surface] : null;
  const surfacePhrase = system ? AUDIT_SURFACE_PHRASES[event.surface] : null;
  const outcome = OUTCOME_LABELS[event.outcome] ?? null;
  const reason = reasonInWords(after.code ?? after.errorCode ?? after.reason);
  const details: ActivityDetail[] = [];

  let action: string;
  switch (event.operation) {
    case 'meeting.import':
      action = 'imported this meeting from Fellow';
      if (after.transcriptStatus === 'empty') details.push({ label: 'Transcript', value: 'None held by Fellow' });
      // Imported outside the automatic window (spec §10): stored, and left for the person to process.
      else if (after.autoProcessed === false) details.push({ label: 'Processing', value: `Left for you — older than ${autoProcessWindowLabel()} at import` });
      break;
    case 'meeting.update':
      action = 'updated this meeting from Fellow';
      break;
    case 'meeting.summarise.request':
      action = 'requested a summary';
      break;
    case 'meeting.process.request': {
      const imported = numberOf(after.fellowTodosImported);
      action = after.summaryQueued === true || imported === null || imported === 0
        ? 'processed this meeting'
        : `processed this meeting, importing ${plural(imported, 'Fellow action item', 'Fellow action items')} as to-dos`;
      break;
    }
    case 'meeting.summarise':
      if (event.outcome === 'success') {
        action = 'generated the summary and its to-dos';
        const keyPoints = numberOf(after.keyPoints);
        const decisions = numberOf(after.decisions);
        if (keyPoints !== null) details.push({ label: 'Key points', value: String(keyPoints) });
        if (decisions !== null) details.push({ label: 'Decisions', value: String(decisions) });
        if (typeof after.model === 'string') details.push({ label: 'Model', value: after.model });
      } else if (event.outcome === 'refused') {
        action = `did not generate a summary because ${reason}`;
      } else {
        action = `could not generate the summary because ${reason}`;
      }
      break;
    case 'meeting.slack_context.request':
      // Why not "asked Calis to look in Slack": the actor is Calis for a request from the command line or a
      // worker, so the line would name one party twice; the admin audit labels the same operation this way.
      action = 'requested a Slack check';
      // The one choice a request makes (owner ruling R7); older rows also counted the channels chosen.
      if (after.includeDms === false) details.push({ label: 'Direct messages and group chats', value: 'Left out of this check' });
      break;
    case 'meeting.slack_context':
      if (event.outcome === 'success') {
        const findings = asRecord(after.findings);
        const counts = SLACK_FINDING_KINDS.map((kind) => [kind, numberOf(findings[kind]) ?? 0] as const);
        const total = counts.reduce((sum, [, count]) => sum + count, 0);
        const messages = numberOf(after.messagesSent);
        if (after.unchanged === true) {
          action = 'checked Slack again; nothing new';
        } else {
          const found = total === 0 ? 'found nothing to add' : `found ${plural(total, 'finding', 'findings')}`;
          action = messages === null ? `checked this meeting against Slack and ${found}` : `compared this meeting with ${plural(messages, 'Slack message', 'Slack messages')} and ${found}`;
          for (const [kind, count] of counts) if (count > 0) details.push({ label: SLACK_FINDING_KIND_LABELS[kind], value: String(count) });
          if (after.partial !== undefined && after.partial !== null && after.partial !== false) details.push({ label: 'Coverage', value: 'Partial — not everything could be read' });
        }
        // How Slack was read (owner ruling R8 §7), from the row's counts and flags alone — an unchanged
        // re-check searched and judged too. Rows from before these were recorded have none.
        const steps = slackStepsOf(after);
        if (steps) details.push({ label: 'How', value: slackStepsText(steps) });
      } else if (event.outcome === 'refused') {
        action = `did not check Slack because ${reason}`;
      } else if (event.outcome === 'skipped') {
        // A later re-check that found a condition no longer held (the only check the worker audits as Skipped).
        action = `did not check Slack again because ${reason}`;
      } else {
        action = `could not check Slack because ${reason}`;
      }
      break;
    case 'slack_finding.dismiss':
      action = 'dismissed a Slack finding';
      break;
    case 'slack_finding.restore':
      action = 'restored a Slack finding';
      break;
    // A person's filing (ADR 0017, design §6.3): how many projects, never which — a name is the project row's label.
    case 'meeting.projects': {
      const filed = filingAction(after, 'this meeting');
      action = filed.action;
      details.push(...filed.details);
      break;
    }
    case 'slack_finding.projects':
      action = filingAction(after, 'a Slack finding').action;
      break;
    case 'todo.import': {
      const imported = numberOf(after.imported);
      action = imported === null
        ? 'imported Fellow’s action items as to-dos'
        : `imported ${plural(imported, 'Fellow action item', 'Fellow action items')} as to-dos`;
      break;
    }
    default:
      action = unknownAction(event, reason);
  }

  const attempts = numberOf(after.attempts);
  if (attempts !== null && event.outcome !== 'success') details.push({ label: 'Attempts', value: String(attempts) });

  return { actorName, system, surfaceLabel, surfacePhrase, action, outcome, details };
}

/** Why: an operation a page does not know still happened; say so in words, never as a key. */
function unknownAction(event: AuditEventDto, reason: string): string {
  const words = event.operation.replace(/[._-]+/gu, ' ').trim();
  if (event.outcome === 'skipped') return `looked and did nothing (${words}) because ${reason}`;
  return event.outcome === 'success' ? `recorded a change (${words})` : `attempted a change (${words}) — ${reason}`;
}

// --- Projects (owner rulings of 2026-10-02; ADR 0017) ---------------------------------------------
//
// Why no sentence or detail carries a name, a former name or an id: a project's name is the row's label, which the
// audit log shows a delegate only as the neutral "Project 1a2b3c4d" (audit.ts DELEGATE_VIEWS), and a share's rows are
// read by both parties — the sharer's feed must never show the recipient's project name (design §5.5). So the sentences
// say what was done to "this project" and how much, from the row's counts and flags alone.

/** Ids in a row's list (`projectIds`), counted; anything else counts as none. */
function countOf(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/** A person's filing of one item: "filed X under 2 projects", or "took X out of its projects" for the empty set. */
function filingAction(after: Record<string, unknown>, item: string): { action: string; details: ActivityDetail[] } {
  const filed = countOf(after.projectIds);
  const removed = numberOf(after.removed);
  const details: ActivityDetail[] = removed !== null && removed > 0 && filed > 0 ? [{ label: 'Taken out', value: plural(removed, 'project', 'projects') }] : [];
  return { action: filed === 0 ? `took ${item} out of its projects` : `filed ${item} under ${plural(filed, 'project', 'projects')}`, details };
}

/** What a merge, its Undo or a delete moved, as counts — only those it moved. */
function movedDetails(after: Record<string, unknown>): ActivityDetail[] {
  const rows: ReadonlyArray<readonly [string, string]> = [
    ['todos', 'To-dos'],
    ['meetings', 'Meetings'],
    ['findings', 'Slack findings'],
    ['matters', 'Slack matters'],
    ['aliases', 'Other names'],
    ['shares', 'Shares'],
    ['closedShares', 'Shares closed'],
    ['reopenedShares', 'Shares reopened'],
  ];
  const details: ActivityDetail[] = [];
  for (const [key, label] of rows) {
    const count = numberOf(after[key]);
    if (count !== null && count > 0) details.push({ label, value: String(count) });
  }
  return details;
}

/**
 * What an Undo took off the project the merge had brought it to, as counts, said from that project's side. Why its own
 * labels and not `movedDetails`': on the survivor's feed the rows left this project, so "To-dos: 2" would read as rows
 * that arrived; and the shares an Undo reopens stand on the project it brought back, never on this one, so they are not
 * counted here.
 */
function takenBackDetails(after: Record<string, unknown>): ActivityDetail[] {
  const rows: ReadonlyArray<readonly [string, string]> = [
    ['todos', 'To-dos taken back'],
    ['meetings', 'Meetings taken back'],
    ['findings', 'Slack findings taken back'],
    ['matters', 'Slack matters taken back'],
    ['shares', 'Shares taken back'],
  ];
  const details: ActivityDetail[] = [];
  for (const [key, label] of rows) {
    const count = numberOf(after[key]);
    if (count !== null && count > 0) details.push({ label, value: String(count) });
  }
  return details;
}

/**
 * A row of a project's history as the route answers it (`ProjectActivityItemDto`), or a bare audit row: what the route
 * resolved beside it — the withheld flag on a foreign actor, the colleague a share entry concerns, the other project a
 * merge concerns — is optional here, so a row without it reads as it always did.
 */
export type ProjectActivityEvent = AuditEventDto & {
  actor: AuditEventDto['actor'] & { withheld?: boolean; administrator?: boolean };
  colleague?: ProjectActivityItemDto['colleague'];
  otherProject?: ProjectActivityItemDto['otherProject'];
};

/**
 * The stated withholding for a colleague the resolver no longer admits (design-system 04-data §6.4: "an actor no longer
 * admitted is a withheld name, not an anonymous one"; the owner's ruling D33 of 2026-10-10, "A colleague (no longer has
 * access)"). Shown only where the server said `withheld` — never re-derived here — and told apart from the plain "A
 * colleague" an account that never gave a name reads as. Why from `src/shared/text.ts` (the owner's ruling of 2026-10-11,
 * "A colleague (no longer has access)" everywhere): the server now writes the same words on a share's party for the
 * shares table and the People tab, and one constant keeps the history's words and theirs the same.
 */
export { WITHHELD_COLLEAGUE, WITHHELD_COLLEAGUE_OBJECT };

/** Who a share entry concerns, mid-sentence (D36): their name; "a colleague" when they gave none or the row says nothing; else withheld. */
function colleagueWords(colleague: ProjectActivityEvent['colleague']): string {
  if (!colleague) return 'a colleague';
  if (colleague.withheld) return WITHHELD_COLLEAGUE_OBJECT;
  return colleague.name ?? 'a colleague';
}

/**
 * A recipient's `project.share_unmerge_in` in words, by its reason (`own`: no reason word, their own Unmerge), each a whole
 * sentence (05-content §8): `shown` as the recipient's own list and a bare row read it, `withheld` as the sharer's feed
 * reads it where the server says the recipient's projects are withheld (`otherProject.withheld`; the owner's ruling of
 * 2026-10-11 on the readings Phase 2d left, "'(withheld)' on related lines").
 */
const UNMERGE_IN_SENTENCES: Readonly<Record<'own' | 'merged' | 'unmerged' | 'deleted', { shown: string; withheld: string }>> = {
  own: {
    shown: 'undid merging this shared project into one of their own',
    withheld: 'undid merging this shared project into one of their own (withheld)',
  },
  merged: {
    shown: 'merged their project holding this shared project into another of their own, which withdrew the merge-back',
    withheld: 'merged their project (withheld) holding this shared project into another of their own (withheld), which withdrew the merge-back',
  },
  unmerged: {
    shown: 'undid merging their project holding this shared project into another of their own, which withdrew the merge-back',
    withheld: 'undid merging their project (withheld) holding this shared project into another of their own (withheld), which withdrew the merge-back',
  },
  deleted: {
    shown: 'deleted their project holding this shared project, which unmerged it',
    withheld: 'deleted their project (withheld) holding this shared project, which unmerged it',
  },
};

/** A string field of a row's snapshot, or null. */
function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * The sentence for one row of a project's history (the record page's R4; 06-modules §5). `projectId` is the project whose
 * feed this is: an Undo's row is written against the project it brought back, and the survivor's feed lists it too (the
 * route reads it by `after.from`), where it must be said from the survivor's side — the reader of a bare row passes none.
 */
export function describeProjectActivity(event: ProjectActivityEvent, projectId?: string): ActivityLine {
  const after = asRecord(event.after);
  const before = asRecord(event.before);
  const system = event.surface !== 'web';
  // Why the server's flag and never a guess from the label (04-data §6.4, "the server decides what is withheld and says
  // so"; ruling D33): a plain "A colleague" is also what an account that never gave a name reads as. Why the role for an
  // administrator's ending of a share by deleting an account (the owner's ruling of 2026-10-11, "An administrator"): the
  // server says so (`actor.administrator`) and sends no name; the audit log keeps it for its own readers.
  const actorName = system
    ? 'Calis'
    : event.actor.administrator === true
      ? AN_ADMINISTRATOR
      : event.actor.withheld === true
        ? WITHHELD_COLLEAGUE
        : actorDisplayName(event.actor.label);
  const surfaceLabel = system ? AUDIT_SURFACE_LABELS[event.surface] : null;
  const surfacePhrase = system ? AUDIT_SURFACE_PHRASES[event.surface] : null;
  const outcome = OUTCOME_LABELS[event.outcome] ?? null;
  const reason = reasonInWords(after.code ?? after.errorCode ?? after.reason);
  const details: ActivityDetail[] = [];
  // D36 (the Calis conformity re-audit of 2026-10-10; 04-data §6.1, the Object): a share entry names its colleague as
  // the route resolved them for this reader, and a merge the other project by the name the owner's store holds now.
  const who = colleagueWords(event.colleague);
  const withheldColleague = event.colleague?.withheld === true;
  const otherName = event.otherProject?.name ?? null;

  let action: string;
  switch (event.operation) {
    case 'project.create':
      action = 'created this project';
      break;
    case 'project.rename': {
      // Why both names, from the row (D35; 04-data §6.5, "the field, the value before, and the value after", and which
      // is now in force without inference — the words "from" and "to" carry it, never position alone): the row holds
      // both, and only the owner reads a project's own rows (a recipient's view lists their acts on the share alone).
      const was = textOf(before.name);
      const now = textOf(after.name);
      if (was !== null && now !== null) action = after.undo === true ? `renamed this project back from “${was}” to “${now}”` : `renamed this project from “${was}” to “${now}”`;
      else action = 'renamed this project';
      if (after.alias === true) details.push({ label: 'Old name', value: 'Still finds this project' });
      if (after.reclaimed === true) details.push({ label: 'New name', value: 'One of this project’s former names' });
      break;
    }
    case 'project.merge':
      // An absorbed project's name is kept as one of this project's other names ("Also known as") until it is forgotten.
      action = event.otherProject === undefined || event.otherProject === null
        ? 'merged another project into this one'
        : otherName !== null
          ? `merged “${otherName}” into this one`
          : 'merged another project, whose name is no longer kept, into this one';
      details.push(...movedDetails(after));
      break;
    case 'project.unmerge':
      // Why the survivor's own sentence (the review of the Phase 2c landing, 2026-10-10; 04-data §6.4 and §6.6): an Undo
      // takes its rows, its absorbed name and the shares the merge carried off the project the merge brought them to, and
      // that project's feed — which says it is whole from its creation — still ended on "merged … into this one" and "shared
      // this project with … when another project merged into it", with nothing after. The route now lists the Undo there
      // too; here it reads as what happened to this project. `otherProject` is then the project the Undo brought back.
      if (projectId !== undefined && event.target.id !== null && event.target.id !== projectId) {
        action = otherName !== null
          ? `undid merging “${otherName}” into this one, which took back what it had brought`
          : event.otherProject
            ? 'undid merging another project, whose name is no longer kept, into this one, which took back what it had brought'
            : 'undid merging another project into this one, which took back what it had brought';
        details.push(...takenBackDetails(after));
        break;
      }
      action = otherName !== null ? `undid this project’s merge into “${otherName}”, bringing it back` : 'undid a merge, bringing this project back';
      details.push(...movedDetails(after));
      break;
    case 'project.delete':
      action = 'deleted this project';
      details.push(...movedDetails(after));
      break;
    case 'project.forget_alias':
      action = after.absorbed === true ? 'forgot the name of a project merged into this one' : 'forgot a former name of this project';
      if (after.undoLost === true) details.push({ label: 'Undo', value: 'That merge can no longer be undone' });
      break;
    // Sharing (rulings S1–S7): the colleague is the route's resolution of the row's recipient id (D36, D33), never the row.
    case 'project.share':
      action =
        after.reason === 'unmerged'
          ? `shared this project with ${who} again when a merge was undone`
          : after.reason === 'merged'
            ? `shared this project with ${who} when another project merged into it`
            : `shared this project with ${who}`;
      break;
    case 'project.share_revoke':
      // Why a sentence for `deleted` (the owner's ruling PM-9 of 2026-10-10, "A row per ended share"): a share ends in
      // passing when its project goes — the sharer's deletion of it, or of their account — and the row says so.
      // Why `merged` names the absorbed project's share and never "this project's" (the review of the Phase 2c landing,
      // 2026-10-10; 04-data §6.1's Object, §6.4's false history): a merge closes the ABSORBED project's share with a
      // recipient who already held a share of this one, and that share of this one stands — the People tab still lists
      // them. The row is listed here because the merge did it, so the sentence names the share that closed (the route
      // resolves the absorbed project beside it, `otherProject`, from the merge row of the same act) and says why, as of
      // then; it claims nothing of the present.
      if (after.reason === 'merged') {
        action = otherName !== null
          ? `closed the share of “${otherName}” with ${who} when it merged into this one, as this project was already shared with them`
          : event.otherProject
            ? `closed the share with ${who} of a project merged into this one, whose name is no longer kept, as this project was already shared with them`
            : `closed the share with ${who} of a project merged into this one, as this project was already shared with them`;
        break;
      }
      action = after.reason === 'deleted' ? `ended this project’s share with ${who} when this project was deleted` : `stopped sharing this project with ${who}`;
      break;
    case 'project.share_merge_in':
      // Why "(withheld)" where the server says so (the owner's ruling of 2026-10-11, "Say a merge-in is withheld";
      // 04-data §6.4, the stated withholding): on the sharer's feed the project it went into is the recipient's, whose name
      // S1 never grants the sharer, so the sentence says it is withheld rather than read as if nothing were left out. The
      // recipient's own list of their acts gets no such word from the server: the project is theirs.
      action = event.otherProject?.withheld === true ? 'merged this shared project into one of their own (withheld)' : 'merged this shared project into one of their own';
      break;
    case 'project.share_unmerge_in': {
      // Why by reason (the review of the Phase 2c landing, 2026-10-10): the recipient's own Unmerge undoes the grouping, but
      // the same row is written when their merge of the project holding it into another of theirs withdrew the sharer's
      // merge-back (`merged` — the share stays merged in, under the survivor: nothing was undone), and when they deleted
      // that project (`deleted` — the grouping ended with it). One sentence for the three said "undid" of two acts that undid
      // nothing, and hid the deletion. And `unmerged` (the owner's ruling of 2026-10-11, "Write a row"): their Undo of such
      // a merge regrouped the share back and withdrew a merge-back the sharer had given against the survivor since.
      // Why "(withheld)" where the server says so (the owner's ruling of 2026-10-11 on the readings Phase 2d left,
      // "'(withheld)' on related lines"; 04-data §6.4, the stated withholding): every project of the recipient's these
      // sentences mention is theirs, whose name S1 never grants the sharer, so on the sharer's feed each says it is
      // withheld, as the merge-in does — after the project it withholds, never after "this shared project", the sharer's
      // own. The recipient's own list of their acts gets no such word from the server: the projects are theirs.
      const sentences = UNMERGE_IN_SENTENCES[after.reason === 'merged' || after.reason === 'unmerged' || after.reason === 'deleted' ? after.reason : 'own'];
      action = event.otherProject?.withheld === true ? sentences.withheld : sentences.shown;
      break;
    }
    case 'project.share_leave':
      // PM-9 as above: the recipient's account was deleted — by this row's actor, the administrator — which ended the
      // share in passing; the sharer's history shows that end, never "left", which the colleague did not do.
      action = after.reason === 'deleted' ? `ended this project’s share with ${who} by deleting their account` : 'left this shared project';
      break;
    case 'project.share_merge_back':
      action = withheldColleague ? `merged back work on this project by ${WITHHELD_COLLEAGUE_OBJECT}` : `merged back ${who}’s own work on this project`;
      break;
    case 'project.share_unmerge_back':
      action = withheldColleague ? `undid merging back work on this project by ${WITHHELD_COLLEAGUE_OBJECT}` : `undid merging back ${who}’s own work on this project`;
      break;
    // The landing backfill (owner ruling 7, "Re-summarise recent meetings now"): written by the scheduler as the system,
    // against the refresh it queued — counts of meetings and jobs, never a meeting's title or the refresh's id.
    case 'project.backfill': {
      action = 'refreshed recent meetings to sort them into projects';
      for (const [key, label] of [['meetings', 'Meetings'], ['summariesQueued', 'Summaries queued'], ['joined', 'Already queued']] as const) {
        const count = numberOf(after[key]);
        if (count !== null && count > 0) details.push({ label, value: String(count) });
      }
      break;
    }
    case 'meeting.projects': {
      const filed = filingAction(after, 'a meeting');
      action = filed.action;
      details.push(...filed.details);
      break;
    }
    case 'slack_finding.projects':
      action = filingAction(after, 'a Slack finding').action;
      break;
    case 'assistant.matter_projects':
      action = filingAction(after, 'a matter from the Slack day').action;
      break;
    default:
      action = unknownAction(event, reason);
  }

  return { actorName, system, surfaceLabel, surfacePhrase, action, outcome, details };
}

/**
 * What a project's history holds, said above it (04-data §6.5, "the grain MUST be stated" — and consistent; §6.6; the
 * re-audit's D31): the rows written against the project — its own changes and its shares — and a person's own filing of
 * a Slack-day matter, whose one row stands on one project the change touched (the first it added, else the first it took
 * out: `routes/assistant.ts`), so a filing under two projects is on one feed of the two. Not listed: a meeting, to-do or
 * Slack finding filed under it, recorded against that item, and every filing Calis makes — the summary's, the check's,
 * the daily sweep's, the brief's and the sorting pass's write no row against the project, only counts on their own row.
 * Why it says all of that (the review of the Phase 2c landing, 2026-10-10): the sentence it replaced said "matters from
 * the Slack day filed under it" are listed, which held only for a person's filing on its first project — a project Calis
 * made from the Slack day showed its creation, none of the matters filed under it, and then called itself whole. The
 * reading that shows less was taken: the grain restated, no new row written.
 */
export const PROJECT_ACTIVITY_GRAIN = 'Listed here: changes to this project and its shares, and your own filing of a Slack-day matter under it or out of it — on one project only where the change touched several. Not listed: meetings, to-dos and Slack findings filed under it, or anything Calis filed here.';

/** How much history a feed holds, in its own words (04-data §6.6, §6.7): its empty state, and the line that closes it. */
export interface ActivityExtent {
  empty: { title: string; description: string };
  closing: string;
}

const DAY_MS = 86_400_000;

/**
 * A project's history's own account of its extent (04-data §6.6, "the feed MUST say whether it is complete"; §6.7, a
 * feed whose history begins later than its record "MUST say from when it is recorded"; the re-audit's D31). Complete
 * when its creation is among the entries — every project made since Calis recorded its own creations has that row (a
 * person's always had). Older than the retention window: the window is why its start is missing. Otherwise the project
 * was made by Calis before its creations were recorded, and the feed says so, with the date, rather than call itself
 * whole. Pure: `now` and the zone are the caller's.
 */
export function projectActivityExtent(project: { id: string; createdAt: string }, now: Date, timeZone: string) {
  return (data: { items: readonly AuditEventDto[]; retentionDays: number }, capped: boolean): ActivityExtent => {
    const created = data.items.some((item) => item.operation === 'project.create' && item.target.id === project.id);
    const olderThanWindow = Date.parse(project.createdAt) < now.getTime() - data.retentionDays * DAY_MS;
    const createdOn = formatDate(project.createdAt, { timeZone });
    const empty = olderThanWindow
      ? {
          title: pluralise(data.retentionDays, { one: 'No activity in the last day', other: 'No activity in the last # days' }),
          description: pluralise(data.retentionDays, { one: 'Activity is kept for # day, and this project is older.', other: 'Activity is kept for # days, and this project is older.' }),
        }
      : { title: 'Nothing recorded yet', description: `This project was created on ${createdOn}, before Calis recorded the projects it creates. Changes to it from now on are listed here.` };
    const closing = capped
      ? 'Older entries within the window exist beyond the 200 shown.'
      : created
        ? 'This is the whole history of this project, from its creation.'
        : olderThanWindow
          ? pluralise(data.retentionDays, { one: 'Activity is kept for # day; this project is older, so its earlier history is not shown.', other: 'Activity is kept for # days; this project is older, so its earlier history is not shown.' })
          : `This project was created on ${createdOn}, before Calis recorded the projects it creates; everything since is shown.`;
    return { empty, closing };
  };
}

/**
 * A shared project's Activity tab, in words (the owner's ruling D32 of 2026-10-10, "History shown as withheld"; 04-data
 * §6.4 and 06-modules §5.7): the project's history is its sharer's and is denied in place — present, said so, never an
 * empty feed under the region's name — and the reader's own acts on the share follow as a list labelled as theirs.
 * Nothing new crosses: the sharer's name is the one the page's subtitle already shows, and none when it reads "A
 * colleague" (not admitted, or never named), so the label is never spliced into a sentence.
 */
export function sharedActivityWords(sharerName: string | null, view: 'own_acts' | 'withheld') {
  // Why the withheld words too (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere): the
  // server now names a sharer it no longer admits so on the page's subtitle and People tab, and neither label is a name
  // to splice into a sentence.
  const named = sharerName !== null && sharerName !== UNNAMED_COLLEAGUE && sharerName !== WITHHELD_COLLEAGUE;
  return {
    deniedTitle: 'This project’s history is withheld',
    deniedDescription: named
      ? `It belongs to ${sharerName}, who shared the project with you; a share does not include its history.`
      : 'It belongs to the colleague who shared the project with you; a share does not include its history.',
    ownHeading: 'Your activity on this shared project',
    // Why "this colleague no longer has access" (the owner's ruling of 2026-10-11 on the readings Phase 2d left, "Align
    // the other tabs' words"): the People tab says the state as "This colleague no longer has access. Nothing crosses
    // until they have access again", and this tab now says the same fact in the same words; it read "while the sharer is
    // not using Calis here" and "until they are back".
    ownEmpty:
      view === 'withheld'
        ? { title: 'Nothing shown while this colleague no longer has access', description: 'Nothing of this share is shown until they have access again, your own acts on it included.' }
        : // Why no "or leaving it" (the review of the Phase 2c landing, 2026-10-10): a recipient who leaves no longer reaches
          // the shared page or its activity (`shareOfProjectForRecipient` asks `left_at IS NULL`), so a leave is never
          // listed here, and the sentence promised it.
          { title: 'Nothing done on this share yet', description: 'Merging it into a project of yours, or undoing that, is listed here.' },
  };
}

/**
 * The extent of the reader's own acts on a share: the window, and that these are theirs, never the project's history.
 * Why the closing names what it leaves out rather than call itself everything (04-data §6.6; the review of the Phase 2c
 * landing, 2026-10-10): the reader's merge of their own projects moves a share grouped under the one it absorbs, and an
 * Undo moves it back, yet a row is written on the share only when that merge, or since the owner's ruling of 2026-10-11
 * ("Write a row") its Undo, withdrew the sharer's merge-back — the one change the sharer sees. The reading that shows less
 * was taken: no row is added for the rest, and the list says so.
 */
export function ownActsExtent(view: 'own_acts' | 'withheld', sharerName: string | null) {
  const words = sharedActivityWords(sharerName, view);
  return (data: { retentionDays: number }, capped: boolean): ActivityExtent => ({
    empty: words.ownEmpty,
    closing: capped
      ? 'Older entries within the window exist beyond the 200 shown.'
      : pluralise(data.retentionDays, {
        one: 'These are your acts on this share in the last # day. Moving it between your own projects — by merging them, or by undoing a merge — is listed only where that withdrew the sharer’s merge-back.',
        other: 'These are your acts on this share in the last # days. Moving it between your own projects — by merging them, or by undoing a merge — is listed only where that withdrew the sharer’s merge-back.',
      }),
  });
}
